/*
References:
https://www.w3.org/Graphics/JPEG/itu-t81.pdf pages 30 & 31
https://www.w3.org/TR/png/#3PNGsignature
https://developers.google.com/speed/webp/docs/riff_container
*/
const JPEG_HEADER = [0xff, 0xd8, 0xff];
const JPEG_TRAILER = [0xff, 0xd9];
const PNG_HEADER = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const PNG_IEND = [0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82];
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];

const startsWith = (bytes, signature, offset = 0) => {
  for (let i = 0; i < signature.length; i += 1) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
};

const readBytes = async (file, start, length) => {
  const slice = file.slice(start, start + length);
  const buf = await slice.arrayBuffer();
  return new Uint8Array(buf);
};

// Returns true if structure looks complete, false if truncated/broken,
// null if format is unknown (defer to the decode step).
const checkStructure = async (file) => {
  if (!file || typeof file.size !== 'number' || file.size < 12) return false;
  const head = await readBytes(file, 0, 12);

  if (startsWith(head, JPEG_HEADER)) {
    const tail = await readBytes(file, file.size - 2, 2);
    return startsWith(tail, JPEG_TRAILER);
  }

  if (startsWith(head, PNG_HEADER)) {
    if (file.size < PNG_HEADER.length + PNG_IEND.length) return false;
    const tail = await readBytes(file, file.size - 8, 8);
    return startsWith(tail, PNG_IEND);
  }

  if (startsWith(head, RIFF) && startsWith(head, WEBP, 8)) {
    const declared =
      head[4] | (head[5] << 8) | (head[6] << 16) | (head[7] << 24);
    return declared === file.size - 8;
  }

  return null;
};

const decodeWithBitmap = async (file) => {
  try {
    const bitmap = await createImageBitmap(file);
    bitmap.close?.();
    return { ok: true };
  } catch {
    return { ok: false };
  }
};

const decodeWithImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: true });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: false });
    };
    img.src = url;
  });

export const validateImageDecodable = async (file) => {
  try {
    const structural = await checkStructure(file);
    if (structural === false) return { ok: false };
  } catch {
    // ignore
  }

  if (typeof createImageBitmap === 'function') {
    return decodeWithBitmap(file);
  }
  return decodeWithImage(file);
};
